import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "./Login.css";

function LogoMark({ size = 32 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className="login-brand__mark"
    >
      <path
        d="M4 20V8.5C4 7.67 4.41 6.9 5.1 6.43L11.1 2.34C11.65 1.97 12.35 1.97 12.9 2.34L18.9 6.43C19.59 6.9 20 7.67 20 8.5V20"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M9 20V13.5C9 12.67 9.67 12 10.5 12H13.5C14.33 12 15 12.67 15 13.5V20"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path
        d="M7.5 10.5H16.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function InputIconMail() {
  return (
    <svg
      className="login-field__icon"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path d="M4 6h16v12H4V6Z" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="m4 7 8 6 8-6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function InputIconLock() {
  return (
    <svg
      className="login-field__icon"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M7 11V8a5 5 0 0 1 10 0v3"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path
        d="M6 11h12v9H6v-9Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function Login() {
  const { login, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);

  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const em = searchParams.get("email");
    if (em) setEmail(em);
    const v = searchParams.get("verify");
    if (v === "sent") {
      setInfo(
        `Check your inbox${em ? ` (${em})` : ""} for a verification link. You must verify before signing in.`,
      );
    } else if (v === "required") {
      setInfo(
        "Verify your email to continue. Use the link we sent or resend below.",
      );
    }
  }, [searchParams]);

  const handleResend = async () => {
    const target = (email || searchParams.get("email") || "").trim();
    if (!target) {
      setError("Enter your email, then click resend verification.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await fetch("/api/resend-verification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: target }),
      });
      const data = res.ok ? await res.json().catch(() => ({})) : await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          data?.detail || data?.error || "Could not send verification email",
        );
      }
      setInfo("If this account exists and is still unverified, we sent another email.");
    } catch (e) {
      setError(e.message || "Resend failed");
    } finally {
      setLoading(false);
    }
  };

  // If a user is already authenticated (and verified), don't let them land on /login.
  useEffect(() => {
    if (!authLoading && user && user.email_verified !== false) {
      navigate("/dashboard", { replace: true });
    }
  }, [authLoading, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setLoading(true);

    try {
      await login({ email, password, remember });
      // Route is /dashboard (not /app/dashboard). Using replace avoids back-navigation to /login.
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err.message || "Sign in failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-shell">
      <div className="login-shell__bg" aria-hidden="true">
        <div className="login-shell__blob login-shell__blob--tr" />
        <div className="login-shell__blob login-shell__blob--bl" />
      </div>

      <div className="login-shell__grid">
        <aside className="login-brand">
          <div className="login-brand__logo">
            <LogoMark />
            <span className="login-brand__name">
              Zyft<span className="login-brand__dot">.AI</span>
            </span>
          </div>

          <h1 className="login-brand__title">
            Welcome to your
            <br />
            <span className="login-brand__accent font-serif">
              engagement platform
            </span>
          </h1>

          <p className="login-brand__subtitle">
            Sign in to access your organization's recognition and engagement
            workspace.
          </p>

          <div className="login-brand__bullets">
            <div className="login-brand__bullet">
              <span className="login-brand__bulletDot" />
              <span>Enterprise-grade security</span>
            </div>
            <div className="login-brand__bullet">
              <span className="login-brand__bulletDot" />
              <span>Strict tenant isolation</span>
            </div>
            <div className="login-brand__bullet">
              <span className="login-brand__bulletDot" />
              <span>AI-powered personalization</span>
            </div>
          </div>
        </aside>

        <section className="login-card">
          <div className="login-card__mobileBrand">
            <LogoMark size={26} />
            <span className="login-brand__name login-brand__name--sm">
              Zyft<span className="login-brand__dot">.AI</span>
            </span>
          </div>

          <div className="login-card__header">
            <h2 className="login-card__title">Sign In</h2>
            <p className="login-card__hint">
              Access your company's engagement workspace
            </p>
          </div>

          {info && (
            <div
              className="login-card__hint"
              role="status"
              style={{ marginTop: "-4px", marginBottom: "8px", lineHeight: 1.5 }}
            >
              {info}{" "}
              <button
                type="button"
                onClick={handleResend}
                style={{
                  cursor: loading ? "wait" : "pointer",
                  background: "none",
                  border: "none",
                  padding: 0,
                  marginLeft: "4px",
                  font: "inherit",
                  textDecoration: "underline",
                  color: "#2563eb",
                }}
              >
                Resend verification email
              </button>
            </div>
          )}

          {error && (
            <div className="login-error" role="alert">
              <div className="login-error__icon" aria-hidden="true">
                !
              </div>
              <p className="login-error__text">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            <div className="login-field">
              <label className="login-field__label" htmlFor="email">
                Email Address
              </label>
              <div className="login-field__control">
                <InputIconMail />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                />
              </div>
            </div>

            <div className="login-field">
              <label className="login-field__label" htmlFor="password">
                Password
              </label>
              <div className="login-field__control">
                <InputIconLock />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>
            
            <div className="login-row">
              <label className="login-remember">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                />
                <span>Remember me</span>
              </label>

              <a className="login-forgot" href="mailto:support@zyft.ai?subject=Password%20Reset%20Help">
                Forgot password?
              </a>
            </div>

            <button className="login-submit" type="submit" disabled={loading}>
              {loading ? (
                "Signing in..."
              ) : (
                <>
                  Sign In <span aria-hidden="true">→</span>
                </>
              )}
            </button>
          </form>

          <div className="login-card__footer">
            <p className="login-card__signup">
              Don&apos;t have an account? <Link to="/signup">Sign up</Link>
            </p>
          </div>

          <div className="login-card__fineprint">
            Protected by enterprise-grade encryption and security
          </div>
        </section>
      </div>
    </div>
  );
}
